import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2, LogOut, FolderOpen, MessageSquare, ChevronRight, Clock, Siren } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TX } from "@/pages/myCasesText";
import { CentralClock } from "@/components/common/CentralClock";

const KEY = "dsa_portal_session";
const loadSession = () => {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) || "null");
    return s && new Date(s.expires_at) > new Date() ? s : null;
  } catch { return null; }
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }) : "");

function LoginForm({ x, lang, onSession }) {
  const [email, setEmail] = useState("");
  const [priv, setPriv] = useState(false);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const request = async () => {
    setBusy(true);
    try {
      await api.post("/portal/request-code", { email, accept_privacy: priv, accept_terms: terms, language: lang });
      setCode("");
      setOtpOpen(true);
      setCooldown(60);
    } catch (e) {
      toast.error(apiErr(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (value) => {
    setBusy(true);
    try {
      const r = await api.post("/portal/verify", { email, code: value });
      localStorage.setItem(KEY, JSON.stringify(r.data));
      setOtpOpen(false);
      onSession(r.data);
    } catch (e) {
      toast.error(x.badCode);
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const valid = /\S+@\S+\.\S+/.test(email) && priv && terms;
  const check = "mt-0.5 border-zinc-700 data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600";
  return (
    <div className="mx-auto w-full max-w-md rounded-2xl border border-zinc-800/70 bg-[#111113] p-7" data-testid="portal-login">
      <div className="mb-5 grid h-11 w-11 place-items-center rounded-xl bg-blue-600/15 text-blue-400"><Mail size={20} /></div>
      <h1 className="text-2xl font-bold tracking-tight text-zinc-50">{x.title}</h1>
      <p className="mt-2 text-[14px] text-zinc-400">{x.intro}</p>
      <Input data-testid="portal-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={x.email}
        onKeyDown={(e) => e.key === "Enter" && valid && request()} className="mt-6 h-11 border-zinc-800 bg-zinc-950 text-[14px] text-zinc-100" />
      <label className="mt-4 flex cursor-pointer items-start gap-2.5 text-[13px] text-zinc-400">
        <Checkbox data-testid="portal-accept-privacy" checked={priv} onCheckedChange={(v) => setPriv(!!v)} className={check} />
        <span>{x.privA} <a href="/privacy" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">{x.privL}</a>{x.privB}</span>
      </label>
      <label className="mt-3 flex cursor-pointer items-start gap-2.5 text-[13px] text-zinc-400">
        <Checkbox data-testid="portal-accept-terms" checked={terms} onCheckedChange={(v) => setTerms(!!v)} className={check} />
        <span>{x.termsA} <a href="/terms" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">{x.termsL}</a>{x.termsB}</span>
      </label>
      <button data-testid="portal-request-code" onClick={request} disabled={!valid || busy}
        className="mt-6 inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-blue-600 text-[14px] font-semibold text-white transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40">
        {busy && !otpOpen && <Loader2 size={16} className="animate-spin" />} {x.send}
      </button>

      <Dialog open={otpOpen} onOpenChange={setOtpOpen}>
        <DialogContent className="border-zinc-800 bg-[#111113] text-zinc-100 sm:max-w-md" data-testid="portal-otp-dialog">
          <DialogHeader>
            <DialogTitle>{x.otpTitle}</DialogTitle>
            <DialogDescription className="text-zinc-400">{x.otpDesc.replace("{email}", email)}</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-2">
            <InputOTP data-testid="portal-otp-input" maxLength={6} value={code} disabled={busy} autoFocus
              onChange={(v) => { setCode(v.replace(/\D/g, "")); if (v.length === 6) verify(v); }}>
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 border-zinc-700 text-lg" />)}
              </InputOTPGroup>
            </InputOTP>
          </div>
          <div className="flex items-center justify-between text-[12px] text-zinc-500">
            <span className="flex items-center gap-1"><Clock size={12} /> {x.valid}</span>
            <button data-testid="portal-resend" disabled={cooldown > 0 || busy} onClick={request} className="text-blue-400 hover:underline disabled:text-zinc-600 disabled:no-underline">
              {cooldown > 0 ? `${x.resend} (${cooldown}s)` : x.resend}
            </button>
          </div>
          <button data-testid="portal-verify" onClick={() => verify(code)} disabled={code.length !== 6 || busy}
            className="mt-2 inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 text-[14px] font-semibold text-white hover:bg-blue-500 disabled:opacity-40">
            {busy && <Loader2 size={15} className="animate-spin" />} {x.verify}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CaseRow({ to, testid, icon: Icon, title, sub, badge, color, closed }) {
  return (
    <a href={to} data-testid={testid}
      className={`group flex items-center gap-4 rounded-xl border px-4 py-3.5 transition-colors ${closed ? "border-zinc-800/60 bg-zinc-900/30 hover:border-zinc-700" : "border-blue-500/30 bg-blue-600/[0.07] hover:border-blue-400/60"}`}>
      <Icon size={18} className={closed ? "text-zinc-600" : "text-blue-400"} />
      <div className="min-w-0 flex-1">
        <div className={`font-mono text-[14px] ${closed ? "text-zinc-400" : "text-zinc-50"}`}>{title}</div>
        <div className="truncate text-[12px] text-zinc-500">{sub}</div>
      </div>
      <span className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold" style={{ background: `${color || "#71717a"}22`, color: color || "#a1a1aa" }}>{badge}</span>
      <ChevronRight size={16} className="text-zinc-600 transition-transform group-hover:translate-x-0.5" />
    </a>
  );
}

function CaseList({ x, lang, data }) {
  const rRow = (r) => (
    <CaseRow key={r.reference} to={r.decision_token ? `/decision/${r.decision_token}` : "#"} testid={`portal-report-${r.reference}`} icon={FolderOpen}
      title={r.reference} sub={`${x.report} · ${r.platform || "—"} · ${fmt(r.created_at)}`} closed={r.closed}
      badge={r.status_labels?.[lang] || r.status_labels?.en || r.status} color={r.status_color} />
  );
  const dRow = (d) => (
    <CaseRow key={d.token} to={`/dispute/${d.token}`} testid={`portal-dispute-${d.token}`} icon={MessageSquare}
      title={d.report_reference} sub={`${x.dispute} · ${fmt(d.created_at)}`} closed={d.closed}
      badge={d.not_eligible ? x.notEligible : `${x.phase} ${d.phase}/5`} color={d.closed ? "#71717a" : "#f59e0b"} />
  );
  const active = [...data.reports.filter((r) => !r.closed).map(rRow), ...data.disputes.filter((d) => !d.closed).map(dRow)];
  const closed = [...data.reports.filter((r) => r.closed).map(rRow), ...data.disputes.filter((d) => d.closed).map(dRow)];
  return (
    <div className="space-y-10">
      {data.live_chats?.length > 0 && (
        <section data-testid="portal-live">
          <h2 className="mb-3 flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wider text-red-400"><Siren size={14} className="animate-pulse" /> Emergency line</h2>
          <div className="space-y-2">
            {data.live_chats.map((c) => (
              <a key={c.token} href={`/live/${c.token}`} data-testid={`portal-live-${c.token}`} className="group flex items-center gap-4 rounded-xl border border-red-500/40 bg-red-600/10 px-4 py-3.5 transition-colors hover:border-red-400/70">
                <Siren size={18} className="text-red-400" />
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-semibold text-zinc-50">{x.liveTitle}</div>
                  <div className="truncate text-[12px] text-zinc-400">{c.report_reference || "—"} · {fmt(c.created_at)}</div>
                </div>
                <ChevronRight size={16} className="text-red-400 transition-transform group-hover:translate-x-0.5" />
              </a>
            ))}
          </div>
        </section>
      )}
      <section data-testid="portal-active">
        <h2 className="mb-3 flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wider text-blue-300">
          <span className="h-2 w-2 rounded-full bg-blue-400" /> {x.active} ({active.length})
        </h2>
        <div className="space-y-2">{active.length ? active : <p className="text-[14px] text-zinc-500" data-testid="portal-active-empty">{x.noActive}</p>}</div>
      </section>
      <section data-testid="portal-closed">
        <h2 className="mb-3 text-[13px] font-semibold uppercase tracking-wider text-zinc-500">{x.closed} ({closed.length})</h2>
        <div className="space-y-2">{closed.length ? closed : <p className="text-[14px] text-zinc-600">{x.noClosed}</p>}</div>
      </section>
    </div>
  );
}

export default function MyCasesPage() {
  const { lang } = useLang();
  const x = TX[lang] || TX.en;
  const [session, setSession] = useState(loadSession);
  const [data, setData] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem(KEY);
    setSession(null);
    setData(null);
  }, []);

  useEffect(() => {
    if (!session) return;
    api.get("/portal/cases", { headers: { Authorization: `Bearer ${session.token}` } })
      .then((r) => setData(r.data))
      .catch(() => { toast.error(x.expired); logout(); });
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!session) return undefined;
    const ms = new Date(session.expires_at) - new Date();
    const id = setTimeout(() => { toast.error(x.expired); logout(); }, Math.max(ms, 0));
    return () => clearTimeout(id);
  }, [session, logout, x.expired]);

  return (
    <div className="flex min-h-screen flex-col bg-zinc-950 text-zinc-50">
      <SiteHeader />
      <main className="mx-auto w-full max-w-3xl flex-1 px-5 py-14">
        <div className="mb-10 flex justify-center"><CentralClock /></div>
        {!session ? <LoginForm x={x} lang={lang} onSession={setSession} /> : (
          <div data-testid="portal-dashboard">
            <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">{x.title}</h1>
                <p className="mt-1 text-[13px] text-zinc-500"><span data-testid="portal-session-email" className="text-zinc-300">{session.email}</span> · {x.until} {fmt(session.expires_at)}</p>
              </div>
              <button data-testid="portal-logout" onClick={logout} className="inline-flex items-center gap-2 rounded-lg border border-zinc-800 px-3 py-2 text-[13px] text-zinc-300 hover:bg-zinc-900"><LogOut size={14} /> {x.logout}</button>
            </div>
            {!data ? <div className="grid h-40 place-items-center"><Loader2 className="animate-spin text-zinc-600" /></div> : <CaseList x={x} lang={lang} data={data} />}
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
